import styled from "styled-components";
import { Modal } from "components/Modal";
import { X } from "lucide-react";
import { Trash2 } from "lucide-react";
import { Pen } from "lucide-react";

export const ProfileModalContainer = styled(Modal)`
  .modal-dialog {
    max-width: 640px;
    font-family: "Geist", sans-serif;
  }

  .modal-content {
    padding: 0px 8px;
    border: none;
    border-radius: 16px;
  }

  .modal-body {
    padding: 0;
    margin: 0;
    max-height: 85vh;
    overflow-y: auto;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.card.bg.tertiary || "#f3f4f6"};
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.card.border.quaternary || "#cbd5e1"};
      border-radius: 4px;

      &:hover {
        background: ${({ theme }) => theme.aside.icon.tertiary || "#94a3b8"};
      }
    }
  }

  .gradient-background {
    width: 100%;
    height: 128px;
    background: linear-gradient(180.74deg, #f7fcff 0.63%, #bac6e5 99.37%);
    border-radius: 8px;
  }

  .profile-modal-content {
    padding: 0px 16px;

    .modal-header-section {
      position: relative;
      padding-bottom: 24px;
      border-bottom: 2px dashed #d5d7da;

      .avatar-section {
        display: flex;
        align-items: start;
        justify-content: space-between;

        .avatar-loader,
        .avatar-wrapper {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          border: 1px solid #00000014;
          padding: 4px;
          background: #fff;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
          margin-top: -48px;
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          border-radius: 100%;
          border: 1px solid #00000014;
        }

        .avatar-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 36px;
          font-weight: 600;
        }

        .header-actions {
          display: flex;
          gap: 4px;
          padding-top: 10px;

          .icon-button {
            background: none;
            padding: 4px 6px;
            cursor: pointer;
            border: none;
          }
        }
      }

      /* User Info Section */
      .user-info-section {
        margin-top: 16px;

        .user-name {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 18px;
          color: #181d27;
          font-weight: 600;
          font-family: "Public Sans", sans-serif;
          line-height: 28px;
        }

        .user-company {
          font-size: 14px;
          color: #535862;
          font-family: "Public Sans", sans-serif;
          font-weight: 400;
        }
      }

      /* Info Banner */
      .info-banner {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-radius: 12px;
        animation: slideDown 0.3s ease-out;
        margin: 16px 0px 0px;
        padding: 16px;
        border: 1px solid #e9eaeb;
        background: #ffffff;

        .banner-content {
          display: flex;
          gap: 12px;
          flex: 1;

          .banner-text {
            p {
              font-size: 14px;
              color: #414651;
              font-weight: 500;
              line-height: 20px;
              margin-bottom: 12px;
            }

            .banner-link {
              font-size: 14px;
              color: #1841dd;
              font-weight: 500;
              line-height: 20px;
              text-decoration: none;

              &:hover {
                text-decoration: underline;
              }
            }
          }
        }

        .banner-close {
          background: rgba(255, 255, 255, 0.8);
          border: none;
          font-size: 20px;
          line-height: 1;
          color: ${({ theme }) => theme.card.font.secondary || "#6b7280"};
          cursor: pointer;
          padding: 0;
          margin-left: 12px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s;

          &:hover {
            background: rgba(255, 255, 255, 1);
            color: ${({ theme }) => theme.card.font.primary || "#111827"};
            transform: scale(1.05);
          }
        }
      }

      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    }

    /* Form */
    .profile-form {
      .input-form {
        margin-bottom: 0;

        .input-form-field {
          font-size: 16px;
          color: #0a0d12e5;
          background: #fff;
          border: 1px solid #d5d7da;
          border-radius: 8px;
          padding: 8px 12px;
          transition: all 0.2s ease;
          margin-bottom: 0px;

          &:hover:not(:disabled) {
            border-color: ${({ theme }) =>
              theme.card.border.tertiary || "#d1d5db"};
          }

          &:focus {
            border-color: ${({ theme }) =>
              theme.global.color.primary || "#3b82f6"};
            outline: none;
          }

          &:disabled {
            background: ${({ theme }) => theme.card.bg.tertiary || "#f9fafb"};
            color: ${({ theme }) => theme.card.font.secondary || "#9ca3af"};
            cursor: not-allowed;
            border-color: ${({ theme }) =>
              theme.card.border.quaternary || "#e5e7eb"};
          }

          &::placeholder {
            color: ${({ theme }) => theme.input.placeholder.color || "#9ca3af"};
          }
        }

        textarea.input-form-field {
          resize: vertical;
          min-height: 100px;
        }
      }

      .bio-input {
        flex: 1;
      }

      .verified-status {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #1570ef;
        margin-top: 8px;

        .check-icon {
          width: 16px;
          height: 16px;
        }
      }

      .unverified-message {
        font-size: 12px;
        color: ${({ theme }) => theme.card.font.secondary || "#6b7280"};
        margin-top: 8px;

        .link {
          color: ${({ theme }) => theme.global.color.primary || "#1570EF"};
          text-decoration: underline;
          cursor: pointer;

          &:hover {
            color: #1570ef;
          }
        }
      }

      /* Email Field */
      .email-field-wrapper {
        .email-input {
          margin-bottom: 8px;
        }

        .verified-status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: ${({ theme }) => theme.global.color.success || "#10b981"};
          margin-top: 8px;

          .check-icon {
            width: 16px;
            height: 16px;
          }
        }
      }

      /* Phone Field */
      .phone-field-wrapper {
        .change-phone-container {
          width: 100%;

          .phone-form-field {
            border: 1px solid
              ${({ theme }) => theme.card.border.quaternary || "#e5e7eb"};
            border-radius: 8px;
            padding: 10px 16px 10px 52px;
            font-size: 14px;
            background: ${({ theme }) => theme.card.bg.secondary || "#fff"};
            color: ${({ theme }) => theme.card.font.primary || "#111827"};

            &:focus {
              border-color: ${({ theme }) =>
                theme.global.color.primary || "#3b82f6"};
              outline: none;
            }
          }

          .react-tel-input {
            .flag-dropdown {
              border: 1px solid
                ${({ theme }) => theme.card.border.quaternary || "#e5e7eb"};
              border-radius: 8px 0 0 8px;
              background: ${({ theme }) => theme.card.bg.secondary || "#fff"};
            }
          }

          .phone-form {
            margin-bottom: 0;
          }

          button {
            margin-left: 10px;
          }
        }
      }

      /* Modal Actions */
      .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        padding: 32px 0px 24px;
      }
    }

    .toggle-switch {
      position: relative;
      display: inline-block;
      width: 39px;
      height: 21px;
      margin: 0;

      input {
        opacity: 0;
        width: 0;
        height: 0;

        &:checked + .toggle-slider {
          background-color: #2258f1;
        }

        &:checked + .toggle-slider:before {
          transform: translateX(17px);
        }
      }

      .toggle-slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${({ theme }) =>
          theme.card.border.quaternary || "#cbd5e1"};
        transition: 0.3s;
        border-radius: 24px;

        &:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }
      }
    }
  }
`;

export const ProfileFieldWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  border-bottom: 2px dashed #d5d7da;
  padding: 16px 0px;

  @media (max-width: 540px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const ProfileLabel = styled.h3`
  max-width: 200px;
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  color: #414651;
  font-family: "Public Sans", sans-serif;
  line-height: 20px;
  margin: 0px;
`;

export const ProfileFieldContent = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  gap: 16px;
`;

export const VerifiedFieldContent = styled.div`
  flex: 1;
`;

export const SocialTitleWrapper = styled.div`
  max-width: 200px;
  width: 100%;
`;

export const ProfileDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #535862;
  line-height: 18px;
  margin: 2px 0px 0px;
`;

export const SocialFieldContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SocialItemWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SocialInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SocialActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SocialName = styled.h3`
  font-size: 14px;
  font-weight: 400;
  color: #0a0d12e5;
  font-family: "Public Sans", sans-serif;
  line-height: 20px;
  margin-bottom: 0px;
`;

export const SocialStatus = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #0a0d1299;
  line-height: 18px;
  margin-bottom: 0px;
`;

export const SocialDeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0px;
`;

export const ConnectButton = styled.button`
  font-size: 12px;
  font-weight: 500px;
  color: #0a0d12b2;
  background: #ffffff;
  border: 1px solid #d5d7da;
  border-radius: 8px;
  padding: 8px 10px;
`;

export const CloseIcon = styled(X)`
  width: 24px;
  height: 24px;
  color: #a4a7ae;
`;
export const TrashIcon = styled(Trash2)`
  width: 20px;
  height: 20px;
  color: #a4a7ae;
`;
export const PenIcon = styled(Pen)`
  width: 20px;
  height: 20px;
  color: #a4a7ae;
`;
